import { cva } from 'class-variance-authority'
import { linkVariants } from '#src/components/Button/linkVariants.ts'

export const buttonVariants = cva(
  'relative inline-flex items-center gap-2 whitespace-nowrap ring-offset-white transition-colors focus-visible:outline-hidden focus-visible:ring-zinc-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 dark:ring-offset-zinc-950 dark:focus-visible:ring-zinc-300 [&_svg:not([class*=size-])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: [
          'text-sm',
          'justify-center',
          'rounded-md',
          'focus-visible:ring-2 data-[active]:ring-2 data-[state=open]:ring-2',
          'bg-primary text-primary-foreground hover:bg-primary-hover',
        ],
        destructive: [
          'text-sm',
          'justify-center',
          'rounded-md',
          'focus-visible:ring-2 data-[active]:ring-2 data-[state=open]:ring-2',
          'bg-red-500 text-zinc-50 hover:bg-red-500/90 dark:bg-red-900 dark:text-zinc-50 dark:hover:bg-red-900/90',
        ],
        outline: [
          'text-sm',
          'justify-center',
          'rounded-md shadow-sm',
          'focus-visible:backdrop-brightness-active data-[active]:backdrop-brightness-active data-[state=open]:backdrop-brightness-active',
          'border border-zinc-200 text-zinc-900 hover:backdrop-brightness-hover dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-50 dark:hover:bg-zinc-800',
        ],
        secondary: [
          'text-sm',
          'justify-center',
          'rounded-md',
          'focus-visible:ring-2 data-[active]:ring-2 data-[state=open]:ring-2',
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ],
        ghost: [
          'text-sm',
          'justify-center',
          'rounded-md',
          'focus-visible:backdrop-brightness-active aria-expanded:backdrop-brightness-active data-[active]:backdrop-brightness-active data-[state=open]:backdrop-brightness-active',
          'hover:backdrop-brightness-hover dark:hover:backdrop-brightness-[1.2]',
          'disabled:opacity-50',
        ],
        link: [
          'justify-center',
          'rounded-md',
          'focus-visible:ring-2 data-[active]:ring-2 data-[state=open]:ring-2',
          linkVariants(),
        ],
        underline: [
          'text-sm',
          'justify-start',
          'rounded-none',
          'focus-visible:border-zinc-950 data-[active]:border-zinc-950 data-[state=open]:border-zinc-950',
          'border-b hover:border-zinc-400',
        ],
        // `status` is the interactive twin of `Badge`'s `outline` variant —
        // `RowBadge` and the care-plan/inbox pills are built on it and sit
        // beside real Badges in the same rows. Its geometry compound mirrors
        // Badge's so the two do not desynchronize.
        status: [
          'text-sm leading-5',
          'justify-center',
          'rounded-full',
          'focus-visible:brightness-95 data-[active]:brightness-95 data-[state=open]:brightness-95',
          'overflow-hidden border hover:brightness-95',
        ],
        unstyled: [],
      },
      size: {
        default: 'h-10 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-9 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5',
        lg: 'h-11 rounded-md px-8 has-[>svg]:px-6',
        icon: 'size-10',
        'icon-sm': 'size-9',
        // Compact icon actions inside dense surfaces (code blocks, attachments).
        'icon-xs': 'size-6 rounded-md [&_svg:not([class*=size-])]:size-3.5',
        'icon-lg': 'size-11',
        iconRounded: 'size-10 rounded-full',
        xs: 'h-6 gap-1 px-2 py-0 text-xs',
        '2xs': 'h-5 gap-1 px-2 py-1',
        unstyled: '',
      },
      color: {
        default: '',
        // Dark text is each tint's own hue, light enough to clear 4.5:1 on
        // that tint over the dark background.
        neutral: 'bg-[#C7C5BE]/15 text-[#5D5645] dark:text-[#C7C5BE]',
        green: 'bg-[#70C475]/15 text-[#269B2E] dark:text-[#70C475]',
        gray: 'bg-[#7C7662]/15 text-[#5D5645] dark:text-[#A8A29E]',
        blue: 'bg-[#3484CF]/15 text-[#2B6296] dark:text-[#6AA7E0]',
        red: 'bg-[#F7774F]/15 text-[#CA4E28] dark:text-[#F7774F]',
        orange:
          'bg-[#FFF1E0] text-[#E87C00] dark:bg-[#E87C00]/15 dark:text-[#FF9F3D]',
        yellow: 'bg-[#F2D86B]/20 text-[#A78D20] dark:text-[#F2D86B]',
        teal: 'bg-teal-600 text-white hover:bg-teal-700',
      },
      loading: {
        true: 'transition-none after:absolute after:inset-0 after:z-10 after:animate-pulse after:rounded-md after:bg-zinc-100 dark:after:bg-zinc-800',
        false: '',
      },
      selected: {
        true: 'bg-[hsl(91.76_0%_93%)] dark:bg-active',
        false: '',
      },
    },
    compoundVariants: [
      {
        variant: 'underline',
        size: 'default',
        className: 'h-auto px-0 py-1.5',
      },
      {
        variant: 'link',
        size: 'default',
        className: 'h-auto p-0',
      },
      {
        variant: 'outline',
        size: 'icon',
        className: 'p-2.75',
      },
      {
        variant: 'outline',
        size: 'icon-sm',
        className: 'p-2',
      },
      {
        variant: 'outline',
        size: 'icon-lg',
        className: 'p-3',
      },
      {
        variant: 'outline',
        size: 'iconRounded',
        className: 'p-2.75',
      },
      {
        variant: 'status',
        className:
          'h-6 px-[9px] py-1 font-normal text-xs leading-4 tracking-[-0.12px]',
      },
      // `status`'s own palette is a set of raw hex tints that predates and
      // does not match `Badge`'s. Both resolve to the same `badge-v2-*`
      // tokens, so an interactive pill and a Badge sitting in the same row
      // finally read as one component. Written out one class at a
      // time because Tailwind scans source as text and cannot see a name built
      // from a template literal. `teal` is left alone: it is a solid filled
      // pill (white text on teal-600), not a tinted badge.
      /* ##BadgeV2Dark The redesign swatches invert under `.dark` on their
       * own, but a color's own `dark:` classes out-specify plain utilities.
       * The `dark:` copies of the swatches replace them in `cn`'s merge. */
      {
        variant: 'status',
        color: 'default',
        className:
          'border-badge-v2-neutral-border bg-badge-v2-neutral-bg text-badge-v2-neutral-text dark:border-badge-v2-neutral-border dark:bg-badge-v2-neutral-bg dark:text-badge-v2-neutral-text',
      },
      {
        variant: 'status',
        color: 'neutral',
        className:
          'border-badge-v2-neutral-border bg-badge-v2-neutral-bg text-badge-v2-neutral-text dark:border-badge-v2-neutral-border dark:bg-badge-v2-neutral-bg dark:text-badge-v2-neutral-text',
      },
      {
        variant: 'status',
        color: 'gray',
        className:
          'border-badge-v2-neutral-border bg-badge-v2-neutral-bg text-badge-v2-neutral-text dark:border-badge-v2-neutral-border dark:bg-badge-v2-neutral-bg dark:text-badge-v2-neutral-text',
      },
      {
        variant: 'status',
        color: 'green',
        className:
          'border-badge-v2-green-border bg-badge-v2-green-bg text-badge-v2-green-text dark:border-badge-v2-green-border dark:bg-badge-v2-green-bg dark:text-badge-v2-green-text',
      },
      {
        variant: 'status',
        color: 'blue',
        className:
          'border-badge-v2-blue-border bg-badge-v2-blue-bg text-badge-v2-blue-text dark:border-badge-v2-blue-border dark:bg-badge-v2-blue-bg dark:text-badge-v2-blue-text',
      },
      {
        variant: 'status',
        color: 'red',
        className:
          'border-badge-v2-red-border bg-badge-v2-red-bg text-badge-v2-red-text dark:border-badge-v2-red-border dark:bg-badge-v2-red-bg dark:text-badge-v2-red-text',
      },
      {
        variant: 'status',
        color: 'orange',
        className:
          'border-badge-v2-orange-border bg-badge-v2-orange-bg text-badge-v2-orange-text dark:border-badge-v2-orange-border dark:bg-badge-v2-orange-bg dark:text-badge-v2-orange-text',
      },
      {
        variant: 'status',
        color: 'yellow',
        className:
          'border-badge-v2-yellow-border bg-badge-v2-yellow-bg text-badge-v2-yellow-text dark:border-badge-v2-yellow-border dark:bg-badge-v2-yellow-bg dark:text-badge-v2-yellow-text',
      },
      {
        loading: true,
        variant: 'underline',
        size: 'default',
        className: [
          'border-transparent text-transparent placeholder:text-transparent hover:border-transparent focus-visible:border-transparent disabled:text-transparent',
        ],
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      color: 'default',
      loading: false,
      selected: false,
    },
  }
)

export type ButtonVariant = NonNullable<
  NonNullable<Parameters<typeof buttonVariants>[0]>['variant']
>
